// ABOUTME: In-app registration screen — Kratos registration forced to the passwordless code method.
// ABOUTME: Cleanly transforms the flow (hide password/role, inject language) instead of DOM hacks.

import { Link, useNavigate, useSearchParams } from "react-router"
import type { RegistrationFlow, UiNode, UpdateRegistrationFlowBody } from "@ory/client"
import { useTranslation } from "react-i18next"
import { LangCurrent } from "@/locales"
import { ory } from "@/model/auth/ory"
import { useKratosFlow } from "@/features/auth/useKratosFlow"
import { FlowForm } from "@/features/auth/FlowForm"
import { inputAttrs } from "@/features/auth/nodeMapping"
import { safeReturnTo } from "@/features/auth/redirectGuard"
import { AUTH_LINK_CLASS } from "@/features/auth/authStyles"
import { AuthCard } from "./AuthCard"
import { AuthFlowError } from "./AuthFlowError"
import { AuthLoading } from "./AuthLoading"
import { asFlowBody } from "@/features/auth/flowBody"

// Registration uses the profile method (email + name traits → "Sign up"). Pin the language trait
// to the current UI language and hide it + the internal role field; leave the rest as Kratos sends.
// lowercase locale ("en"/"ru") matches the Kratos identity schema trait used by the standalone
// auth-ui in production (the GraphQL Language enum is uppercase, but that's a separate mapping).
const prepareRegistration =
  (lang: string) =>
  (flow: RegistrationFlow): RegistrationFlow => {
    const nodes = flow.ui.nodes.map((node) => {
      const a = inputAttrs(node)
      if (a?.name === "traits.language") return { ...node, attributes: { ...a, value: lang, type: "hidden" } } as UiNode
      if (a?.name === "metadata_public.role") return { ...node, attributes: { ...a, type: "hidden" } } as UiNode
      return node
    })
    return { ...flow, ui: { ...flow.ui, nodes } }
  }

export const RegistrationPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get("return_to")
  const lang = LangCurrent()

  // Same reasoning as LoginPage: OIDC sign-up must return to THIS host, not the
  // shared Kratos default_browser_return_url (a different host on beta/alpha).
  const kratosReturnTo = new URL(safeReturnTo(returnTo), window.location.origin).href

  const { flow, submit, submitting, failed, restart } = useKratosFlow<RegistrationFlow>({
    createFlow: () => ory.createBrowserRegistrationFlow({ returnTo: kratosReturnTo }),
    getFlow: (id) => ory.getRegistrationFlow({ id }),
    updateFlow: (id, body) =>
      ory.updateRegistrationFlow({
        flow: id,
        updateRegistrationFlowBody: asFlowBody<UpdateRegistrationFlowBody>(body),
      }),
    transformFlow: prepareRegistration(lang),
    onSuccess: (data) => {
      // Password-method registration hands off to email verification; code-method usually
      // creates a session directly (→ default redirect into the app).
      const cont = (data as { continue_with?: { action?: string; flow?: { id: string } }[] }).continue_with
      const verify = cont?.find((c) => c.action === "show_verification_ui")
      if (verify?.flow) {
        const sp = new URLSearchParams({ flow: verify.flow.id })
        if (returnTo) sp.set("return_to", returnTo)
        navigate({ pathname: "/auth/verification", search: sp.toString() }, { replace: true })
        return true
      }
      return false
    },
  })

  // The hidden traits.language node is pinned when the flow is first adopted; a language switch
  // after that would otherwise submit the stale value. Re-stamp it with the current UI language
  // at submit time so the account is created in the language actually selected on this screen.
  const handleSubmit = (body: Record<string, unknown>) => {
    const traits = body.traits
    if (traits && typeof traits === "object") {
      submit({ ...body, traits: { ...(traits as Record<string, unknown>), language: LangCurrent() } })
      return
    }
    submit(body)
  }

  if (failed)
    return (
      <AuthFlowError
        title={t("auth.signUpTitle")}
        onRetry={restart}
      />
    )
  if (!flow) return <AuthLoading />

  return (
    <AuthCard
      title={t("auth.signUpTitle")}
      footer={
        <Link
          to={returnTo ? `/auth/login?return_to=${encodeURIComponent(returnTo)}` : "/auth/login"}
          className={AUTH_LINK_CLASS}
        >
          {t("auth.haveAccount")} {t("auth.signIn")}
        </Link>
      }
    >
      <FlowForm
        flow={flow}
        submitting={submitting}
        onSubmit={handleSubmit}
      />
    </AuthCard>
  )
}
